import { useCallback, useEffect, useLayoutEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

// Tryb nocny dla portalu publicznego (Faza dark-mode, 2026-05-17).
// Pierwsza wizyta -> prefers-color-scheme; po pierwszym kliknięciu klienta
// wybór zostaje zapisany w localStorage i ma pierwszeństwo.
//
// Scope dark mode jest ograniczony do portalu publicznego klasą `.public-dark`
// (zob. tailwind.config.ts -> darkMode: ['class', '.public-dark']). Panel
// staff nie dostaje klasy, więc `dark:` w nim się nie aktywuje, nawet gdyby
// kiedyś ktoś dorzucił dark: utility do wspólnych komponentów.

const STORAGE_KEY = 'public-theme';

export type PublicTheme = 'light' | 'dark';

function readSystemTheme(): PublicTheme {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return 'light';
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

function readStoredTheme(): PublicTheme | null {
  try {
    const v = window.localStorage.getItem(STORAGE_KEY);
    return v === 'light' || v === 'dark' ? v : null;
  } catch {
    return null;
  }
}

export function usePublicTheme(): {
  theme: PublicTheme;
  toggle: () => void;
} {
  const [theme, setTheme] = useState<PublicTheme>(() => readStoredTheme() ?? readSystemTheme());

  // Nasłuchuj zmiany systemowej tylko dopóki klient świadomie nie wybrał.
  // Po wyborze localStorage 'wygrywa' i ignorujemy systemowe zmiany —
  // żeby klient nie został zaskoczony zmianą w połowie rezerwacji.
  useEffect(() => {
    if (readStoredTheme() !== null) return;
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const handler = (e: MediaQueryListEvent) => setTheme(e.matches ? 'dark' : 'light');
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  const toggle = useCallback(() => {
    setTheme((prev) => {
      const next: PublicTheme = prev === 'dark' ? 'light' : 'dark';
      try {
        window.localStorage.setItem(STORAGE_KEY, next);
      } catch {
        /* tryb prywatny / disabled storage — wybór trzyma się tylko w pamięci */
      }
      return next;
    });
  }, []);

  // Aplikuj klasę `.public-dark` na <html>, kiedy portal jest zamontowany
  // w trybie nocnym. Klasa znika przy unmount (klient wraca do panelu staff)
  // albo gdy przełączy z powrotem na jasny. Tailwind ma darkMode skonfigurowany
  // jako `['class', '.public-dark']` więc dark: utility aktywuje się wtedy
  // dla całego drzewa portalu (włącznie z fixed-position lightbox/banner).
  // useLayoutEffect żeby uniknąć migotania białego tła przy hard-refresh.
  useLayoutEffect(() => {
    const root = document.documentElement;
    if (theme === 'dark') {
      root.classList.add('public-dark');
      return () => root.classList.remove('public-dark');
    }
    root.classList.remove('public-dark');
    return undefined;
  }, [theme]);

  return { theme, toggle };
}

export function ThemeToggle({ theme, onToggle }: { theme: PublicTheme; onToggle: () => void }) {
  const { t } = useTranslation();
  const isDark = theme === 'dark';
  return (
    <button
      type="button"
      onClick={onToggle}
      aria-label={isDark ? t('publicBooking.theme.toLight') : t('publicBooking.theme.toDark')}
      title={isDark ? t('publicBooking.theme.toLight') : t('publicBooking.theme.toDark')}
      className="inline-flex h-9 w-9 items-center justify-center rounded-full ring-1 ring-zinc-200 bg-white text-zinc-700 transition hover:bg-zinc-50 hover:text-brand-700 focus:outline-none focus:ring-2 focus:ring-brand-500 dark:bg-zinc-800 dark:text-zinc-200 dark:ring-zinc-700 dark:hover:bg-zinc-700 dark:hover:text-brand-300"
    >
      {isDark ? (
        // Słońce (klik -> wraca do trybu jasnego)
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.7"
          className="h-5 w-5"
          aria-hidden="true"
        >
          <circle cx="12" cy="12" r="4" />
          <path
            strokeLinecap="round"
            d="M12 2.5v2M12 19.5v2M4.5 12h-2M21.5 12h-2M5.6 5.6 4.2 4.2M19.8 19.8l-1.4-1.4M5.6 18.4l-1.4 1.4M19.8 4.2l-1.4 1.4"
          />
        </svg>
      ) : (
        // Księżyc (klik -> przejdź w tryb nocny)
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 24 24"
          fill="currentColor"
          className="h-5 w-5"
          aria-hidden="true"
        >
          <path d="M21 12.79A9 9 0 1 1 11.21 3a7 7 0 0 0 9.79 9.79Z" />
        </svg>
      )}
    </button>
  );
}
